import React from "react";
import { useNavigate } from "react-router-dom";
import { PublicShell } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { NewsletterInline } from "@/components/Newsletter";
import { ContentCard } from "@/components/ContentCard";
import { Clock, BookOpen, Video, Bell } from "lucide-react";

// Course structure is not finalised. Nothing about individual lessons is invented here —
// the page frame is built and clearly marked "Not yet published" until the outline is supplied.
export default function FollowUpSystem() {
  const navigate = useNavigate();
  return (
    <PublicShell>
      {/* Hero */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[900px] px-5 py-20 lg:px-8">
          <div className="flex items-center gap-3">
            <p className="kicker text-xs text-accent">Course</p>
            <span className="kicker rounded-full border border-border bg-card px-3 py-0.5 text-[10px] text-muted" data-testid="course-status-badge">Not yet published</span>
          </div>
          <h1 className="mt-5 font-heading text-[clamp(2.2rem,6vw,3.4rem)] font-extrabold leading-[1.08] text-ink" data-testid="course-title">
            The Follow-Up System
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted">
            Most sales are lost in the gap after the first conversation — not in the pitch. This course is about closing that gap with a follow-up system your business runs every day, whether or not you remember to.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted">
            <span className="flex items-center gap-1.5"><Video className="h-4 w-4" strokeWidth={2} /> Self-paced video</span>
            <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4" strokeWidth={2} /> Lessons — to be announced</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" strokeWidth={2} /> Availability — to be announced</span>
          </div>
        </div>
      </section>

      {/* What it is */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[900px] px-5 py-16 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-ink">What you'll walk away with</h2>
          <p className="mt-3 max-w-2xl text-ink">
            The outcome is simple: a repeatable way to follow up with every lead and customer, so revenue stops depending on you personally chasing it.
          </p>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <ContentCard type="course" testid="course-card-outcome"
              data={{ availability: "In development", outcome: "A follow-up system that runs without you", summary: "The structure, not just the pep talk — mapped to how you actually sell.", format: "Video + templates" }} />
            <ContentCard type="course" testid="course-card-format"
              data={{ availability: "In development", outcome: "Templates you can use the same week", summary: "The messages, timings and triggers, ready to adapt to your business.", format: "Self-paced" }} />
          </div>
          <p className="mt-6 text-sm text-muted">
            Full lesson breakdown is being finalised and will appear here before launch. Nothing on this page is a placeholder number — where a detail isn't confirmed yet, it simply says so.
          </p>
        </div>
      </section>

      {/* Notify */}
      <section>
        <div className="mx-auto max-w-[900px] px-5 py-16 lg:px-8">
          <div className="rounded-xl border border-border bg-card p-8">
            <p className="flex items-center gap-2 font-heading text-lg font-bold text-ink">
              <Bell className="h-5 w-5 text-accent" strokeWidth={2} /> Tell me when it's live
            </p>
            <p className="mt-2 text-sm text-muted">Join the daily and you'll be first to hear the moment the Follow-Up System opens.</p>
            <div className="mt-5 max-w-xl"><NewsletterInline source="course-follow-up" compact /></div>
            <p className="mt-4 text-sm text-muted">
              In the meantime,{" "}
              <button onClick={() => navigate("/blog")} className="font-semibold text-accent underline-offset-4 hover:underline">read the daily</button>{" "}— one real business story every morning.
            </p>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
